/**
 * useChatMessages - Hook for managing chat messages state and API calls
 */
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { auth } from '../../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  streaming?: boolean;
  draft_id?: string | null;
}

interface AddMessageOptions {
  id?: string;
  streaming?: boolean;
  draftId?: string | null;
}

interface StopStreamingOptions {
  messageId?: string;
  draftId?: string | null;
}

export const useChatMessages = (caseId: string | null) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  
  // Streaming state
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const streamingContentRef = useRef('');

  // Load messages for a thread
  const loadThreadMessages = useCallback(async () => {
    if (!activeThreadId) return;
    
    setLoadingMessages(true);
    try {
      const token = auth.getToken();
      const response = await axios.get(
        `${API}/chat/threads/${activeThreadId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [activeThreadId]);

  // Create a new thread
  const createThread = useCallback(async (title?: string): Promise<string> => {
    const token = auth.getToken();
    try {
      const response = await axios.post(
        `${API}/chat/threads`,
        { case_id: caseId, title: title || 'Ny chat' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newThreadId = response.data.id;
      setActiveThreadId(newThreadId);
      return newThreadId;
    } catch (error) {
      console.error('Thread creation error:', error);
      throw error;
    }
  }, [caseId]);

  // Add a user message to the messages array
  const addUserMessage = useCallback((content: string): string => {
    const userMsg: Message = {
      id: `temp_user_${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    return userMsg.id;
  }, []);

  // Add an assistant message
  const addAssistantMessage = useCallback((content: string, options: AddMessageOptions = {}): string => {
    const assistantMsg: Message = {
      id: options.id || `temp_assistant_${Date.now()}`,
      role: 'assistant',
      content,
      streaming: options.streaming || false,
      draft_id: options.draftId || null,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, assistantMsg]);
    return assistantMsg.id;
  }, []);

  // Start streaming (prepare UI)
  const startStreaming = useCallback((): string => {
    const tempId = `temp_assistant_${Date.now()}`;
    streamingContentRef.current = '';
    setStreamingContent('');
    setStreamingMessageId(tempId);
    return tempId;
  }, []);

  // Update streaming content
  const updateStreamingContent = useCallback((content: string): void => {
    streamingContentRef.current = content;
    setStreamingContent(content);
  }, []);

  // Append to streaming content
  const appendStreamingContent = useCallback((chunk: string): void => {
    streamingContentRef.current += chunk;
    setStreamingContent(streamingContentRef.current);
  }, []);

  // Stop streaming and finalize message
  const stopStreaming = useCallback((finalContent: string, options: StopStreamingOptions = {}): void => {
    setStreamingContent('');
    setStreamingMessageId(null);
    streamingContentRef.current = '';
    
    if (finalContent) {
      addAssistantMessage(finalContent, {
        id: options.messageId,
        draftId: options.draftId
      });
    }
  }, [addAssistantMessage]);

  // Clear all messages (when switching threads)
  const clearMessages = useCallback((): void => {
    setMessages([]);
    setStreamingContent('');
    setStreamingMessageId(null);
    streamingContentRef.current = '';
  }, []);

  return {
    // State
    messages,
    loadingMessages,
    activeThreadId,
    streamingContent,
    streamingMessageId,
    streamingContentRef,
    
    // Setters
    setActiveThreadId,
    setMessages,
    
    // Actions
    loadThreadMessages,
    createThread,
    addUserMessage,
    addAssistantMessage,
    startStreaming,
    updateStreamingContent,
    appendStreamingContent,
    stopStreaming,
    clearMessages
  };
};

export default useChatMessages;
